import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { S25Util } from "../../../util/s25-util";
import {
    IngestionPoint,
    IngestionPointCategory,
    INGESTION_CATEGORIES,
    DataIngestionService,
} from "../data.ingestion.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { Table } from "../../s25-table/Table";
import Cell = Table.Cell;
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { UserprefService } from "../../../services/userpref.service";
import { jSith } from "../../../util/jquery-replacement";
import { AlertService } from "../../../services/alert.service";

@TypeManagerDecorator("s25-ng-ingestion-admin")
@Component({
    selector: "s25-ng-ingestion-admin",
    template: `
        <!-- Navigation Tabs -->
        <div class="admin-tabs">
            <button class="admin-tab" [class.active]="currentMode === 'forms'" (click)="setMode('forms')">
                Manage Forms
            </button>
            <button
                class="admin-tab"
                [class.active]="currentMode === 'search-reports'"
                (click)="setMode('search-reports')"
            >
                Search & Reports
            </button>
        </div>

        <!-- Forms Management Mode -->
        @if (currentMode === "forms") {
            @if (!editingForm) {
                <div class="c-margin-bottom--single">
                    <s25-ng-checkbox
                        [parentCd]="cd"
                        [(modelValue)]="includeDeleted"
                        (modelValueChange)="onIncludeDeletedChange()"
                    >
                        Show Deleted Forms
                    </s25-ng-checkbox>
                </div>

                <s25-ng-table
                    [caption]="'Data Ingestion Forms'"
                    [unlimitedWidth]="true"
                    [columnSortable]="false"
                    [dataSource]="dataSource"
                    [hasTotalRowCount]="true"
                    [hasRefresh]="true"
                    [showHeaderWhenNoData]="true"
                ></s25-ng-table>
                <button (click)="addNew()" class="c-margin-top--single aw-button aw-button--outline">
                    Create New Form
                </button>
            }

            @if (editingForm && addingNew) {
                <div class="c-margin-bottom--double">
                    <h2>Create New Form</h2>

                    <div class="c-margin-bottom--single">
                        <label class="ngBlock">
                            Form Name:
                            <input
                                class="c-input"
                                type="text"
                                [(ngModel)]="editingForm.ingestionPointName"
                                placeholder="Enter form name"
                            />
                        </label>
                    </div>

                    <div class="c-margin-bottom--single">
                        <label class="ngBlock">
                            Description (optional):
                            <textarea
                                class="cn-form__control"
                                [(ngModel)]="editingForm.description"
                                placeholder="Enter a description for this form"
                                rows="3"
                            ></textarea>
                        </label>
                    </div>

                    @if (!categoryIn) {
                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Category:&nbsp;
                                <select
                                    class="cn-form__control"
                                    [(ngModel)]="editingForm.category"
                                    (ngModelChange)="onCategoryChange()"
                                >
                                    @for (category of INGESTION_CATEGORIES; track category) {
                                        <option [value]="category">{{ category }}</option>
                                    }
                                </select>
                            </label>
                        </div>
                    } @else {
                        <div class="c-margin-bottom--single">Category: {{ editingForm.category }}</div>
                    }

                    @if (editingForm.category) {
                        <div class="c-margin-bottom--single">
                            <label class="ngBlock">
                                Sub-Category:
                                <s25-ng-ingestion-subcategory-selector
                                    [category]="editingForm.category"
                                    [subCategory]="editingForm.subCat"
                                    [isEditing]="!subCategoryIn"
                                    [onSubCategoryChange]="onSubCategoryChange"
                                ></s25-ng-ingestion-subcategory-selector>
                            </label>
                        </div>
                    }

                    <div class="c-margin-top--single">
                        <s25-ng-button [type]="'primary'" [onClick]="saveNewForm">Create Form</s25-ng-button>
                        <button
                            (click)="cancelEdit()"
                            class="c-margin-left--single aw-button aw-button--danger--outline"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            }

            @if (editingForm && !addingNew) {
                <div class="c-margin-bottom--single">
                    <h2>Edit Form: {{ editingForm.ingestionPointName }}</h2>
                    <button (click)="backToTable()" class="aw-button aw-button--outline">← Back to Forms List</button>
                </div>

                <s25-ng-admin-ingestion-form
                    [ingestionPointId]="editingForm.ingestionPointId"
                    (onSave)="onFormSaved()"
                    (onCancel)="onFormCancelled()"
                ></s25-ng-admin-ingestion-form>
            }
        }

        <!-- Search & Reports Mode -->
        @if (currentMode === "search-reports") {
            <div class="search-reports-admin">
                <h2>Ingestion Search & Reports</h2>
                <p>Search and generate reports from ingestion form submissions</p>

                <s25-ng-ingestion-search-report [initialMode]="'search'"> </s25-ng-ingestion-search-report>
            </div>
        }
    `,
    styles: `
        .admin-tabs {
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
            border-bottom: 2px solid #dee2e6;
            padding-bottom: 10px;
        }

        .admin-tab {
            padding: 10px 20px;
            border: 1px solid #ddd;
            background: #f5f5f5;
            cursor: pointer;
            border-radius: 4px 4px 0 0;
            border-bottom: none;
            font-weight: 500;
        }

        .admin-tab.active {
            background: #007bff;
            color: white;
            border-color: #007bff;
        }

        .admin-tab:hover:not(.active) {
            background: #e9ecef;
        }

        .search-reports-admin {
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
            margin-top: 20px;
        }

        .search-reports-admin h2 {
            margin-top: 0;
            color: #333;
        }

        .search-reports-admin p {
            color: #666;
            margin-bottom: 20px;
        }

        .form-header {
            border-bottom: 2px solid #007bff;
            padding-bottom: 10px;
            margin-bottom: 20px;
        }

        .form-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 5px;
            margin-bottom: 20px;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionAdminComponent implements OnInit {
    @ViewChild(S25TableComponent) table: S25TableComponent;

    @Input() categoryIn?: IngestionPointCategory;
    @Input() subCategoryIn?: string;

    editingForm: IngestionPoint = null;
    addingNew: boolean = false;
    dataSource: Table.Unpaginated;
    dateFormat: string;
    includeDeleted: boolean = false;
    currentMode: "forms" | "search-reports" = "forms";

    // Category options
    INGESTION_CATEGORIES = INGESTION_CATEGORIES;

    constructor(public cd: ChangeDetectorRef) {}

    setMode(mode: "forms" | "search-reports") {
        this.currentMode = mode;
        this.cd.detectChanges();
    }

    async ngOnInit() {
        if (this.subCategoryIn && !this.categoryIn) {
            throw new Error("Sub-category provided without category");
        }

        this.dataSource = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: [
                {
                    id: "ingestionPointId",
                    header: "ID",
                },
                {
                    id: "category",
                    header: "Category",
                },
                {
                    id: "subCat",
                    header: "Sub-Category",
                },
                {
                    id: "ingestionPointName",
                    header: "Form Name",
                },
                {
                    id: "createDate",
                    header: "Created",
                    type: "date",
                },
                {
                    id: "lastModDate",
                    header: "Last Modified",
                    type: "date",
                },
                {
                    id: "enabled",
                    header: "Status",
                },
                GenericTableButtonComponent.Column("Edit", this.edit, "outline"),
                GenericTableButtonComponent.Column("Delete", this.delete, "danger--outline"),
            ],
        };

        S25Util.all({
            dateFormat: UserprefService.getS25Dateformat(),
        }).then((result) => {
            this.dateFormat = result.dateFormat;
            this.cd.detectChanges();
        });
    }

    edit = (row: Table.Row) => {
        this.editingForm = row.data as IngestionPoint;
        this.addingNew = false;
        this.cd.detectChanges();
    };

    addNew = () => {
        this.editingForm = {
            ingestionPointName: "",
            category: this.categoryIn,
            subCat: this.subCategoryIn || "",
            description: "",
            enabled: false,
        };

        // Auto-set category if only one option available and no input provided
        if (!this.categoryIn && INGESTION_CATEGORIES.length === 1) {
            this.editingForm.category = INGESTION_CATEGORIES[0];
        }

        this.addingNew = true;
        this.cd.detectChanges();
    };

    delete = async (row: Table.Row, instance: GenericTableButtonComponent) => {
        const point = row.data as IngestionPoint;

        const confirmed = await AlertService.confirm(
            `<pre>Are you absolutely sure you want to deactivate this form (${point.ingestionPointName})?\n\n` +
                `WARNING: This will:\n` +
                `• Prevent further submissions to this form\n` +
                `• Hide the form from users (it will no longer appear in form lists)\n` +
                `• Preserve all existing form data and submissions\n` +
                `• Preserve all form fields and configuration\n\n` +
                `The form can be reactivated later if needed.</pre>`,
        );

        if (!confirmed) return;

        try {
            await DataIngestionService.deleteIngestionPoint(point.ingestionPointId);
            await this.table?.refresh(true, true);
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError("Failed to deactivate form: " + error);
        }
    };

    cancelEdit = () => {
        this.editingForm = null;
        this.addingNew = false;
        this.cd.detectChanges();
    };

    backToTable = () => {
        this.editingForm = null;
        this.cd.detectChanges();
    };

    saveNewForm = async () => {
        if (!this.editingForm.ingestionPointName?.trim()) {
            S25Util.showError("Form name is required");
            return;
        }

        if (!this.editingForm.category) {
            S25Util.showError("Category is required");
            return;
        }

        try {
            const [resp, error] = await S25Util.Maybe(DataIngestionService.createIngestionPoint(this.editingForm));
            if (error) {
                S25Util.showError(error);
                return;
            }

            this.cancelEdit();
            await this.table?.refresh(true, true);
            this.cd.detectChanges();
        } catch (error) {
            S25Util.showError("Failed to create form: " + error);
        }
    };

    onFormSaved = async () => {
        // Refresh the table and go back to the main view
        await this.table?.refresh(true, true);
        this.backToTable();
    };

    onFormCancelled = () => {
        this.backToTable();
    };

    onCategoryChange = () => {
        this.editingForm.subCat = "";
        this.cd.detectChanges();
    };

    onSubCategoryChange = (subCategory: string) => {
        this.editingForm.subCat = subCategory;
        this.cd.detectChanges();
    };

    onIncludeDeletedChange = async () => {
        // Refresh the table when the includeDeleted checkbox changes
        if (this.table) {
            await this.table.refresh(true, true);
        }
        this.cd.detectChanges();
    };

    getRows = async () => {
        let rows: Table.Row[] = [];
        let points = await DataIngestionService.getIngestionPoints(
            this.categoryIn,
            this.subCategoryIn,
            this.includeDeleted,
        );

        if (points?.length) {
            // Populate subCategoryNames for ENGAGEMENT category
            await DataIngestionService.populateSubCategoryNames(IngestionPointCategory.ENGAGEMENT, points);

            jSith.forEach(points, (idx, point: IngestionPoint) => {
                const cells: Table.Row["cells"] = {};
                for (let i = 0; i < this.dataSource.columns.length; i++) {
                    let column = this.dataSource.columns[i];
                    let cell: Cell = {};

                    if (column.type === "date") {
                        cell.text = S25Datefilter.transform(
                            <string | number>point[column.id as keyof IngestionPoint],
                            this.dateFormat,
                        );
                        cell.sortValue = (point[column.id as keyof IngestionPoint] as string) || "";
                    } else if (column.id === "enabled") {
                        cell.text = point[column.id as keyof IngestionPoint] ? "Enabled" : "Disabled";
                        cell.sortValue = point[column.id as keyof IngestionPoint] ? "1" : "0";
                    } else if (column.id === "subCat") {
                        // Use subCategoryName if available, otherwise fall back to subCat
                        cell.text = point.subCategoryName || (point[column.id as keyof IngestionPoint] as string) || "";
                    } else {
                        cell.text = (point[column.id as keyof IngestionPoint] as string) || "";
                    }
                    cells[column.id] = cell;
                }

                rows.push({
                    id: point.ingestionPointId,
                    name: point.ingestionPointId + "",
                    cells: cells,
                    data: point,
                });
            });
        }

        return {
            rows: rows,
            totalRows: S25Util.parseInt(points?.length ?? 0),
        };
    };
}
